import { useState, useEffect } from 'react';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { TrendingUp, MessageSquare, Users, Clock, ArrowUp, ArrowDown } from 'lucide-react';

// Helper function to format status for display
const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  voteCount: number;
  createdBy: string;
  createdByEmail: string;
  createdByNickname?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

interface AnalyticsData {
  totalRequests: number;
  requestsByStatus: {
    open: number;
    in_progress: number;
    completed: number;
    rejected: number;
  };
  topRequests: FeatureRequest[];
  recentRequests: FeatureRequest[];
  totalVotes: number;
  totalComments: number;
}

interface AnalyticsDashboardProps {
  customerId: string;
  brandColor: string;
  onViewRequest: (request: FeatureRequest) => void;
}

export default function AnalyticsDashboard({ customerId, brandColor, onViewRequest }: AnalyticsDashboardProps) {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalRequests: 0,
    requestsByStatus: { open: 0, in_progress: 0, completed: 0, rejected: 0 },
    topRequests: [],
    recentRequests: [],
    totalVotes: 0,
    totalComments: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const requestsRef = collection(db, 'customers', customerId, 'requests');
        const requestsSnapshot = await getDocs(requestsRef);

        const requests = requestsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as FeatureRequest[];

        // Calculate stats
        const totalRequests = requests.length;
        const requestsByStatus = {
          open: requests.filter(r => r.status === 'open').length,
          in_progress: requests.filter(r => r.status === 'in_progress').length,
          completed: requests.filter(r => r.status === 'completed').length,
          rejected: requests.filter(r => r.status === 'rejected').length,
        };

        const totalVotes = requests.reduce((sum, r) => sum + (r.voteCount || 0), 0);

        // Get top 5 requests by vote count
        const topRequests = [...requests]
          .sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0))
          .slice(0, 5);

        // Get 5 most recent requests
        const recentRequests = [...requests]
          .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
          .slice(0, 5);

        // Get total comments (this would require fetching all comments, for now estimate)
        let totalComments = 0;
        for (const request of requests) {
          const commentsRef = collection(db, 'customers', customerId, 'requests', request.id, 'comments');
          const commentsSnapshot = await getDocs(commentsRef);
          totalComments += commentsSnapshot.size;
        }

        setAnalytics({
          totalRequests,
          requestsByStatus,
          topRequests,
          recentRequests,
          totalVotes,
          totalComments,
        });
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [customerId]);

  const StatCard = ({ icon: Icon, label, value, subtitle, trend }: any) => (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-2">
        <div
          className="p-2 rounded-lg"
          style={{ backgroundColor: `${brandColor}15` }}
        >
          <Icon className="h-5 w-5" style={{ color: brandColor }} />
        </div>
        {trend && (
          <div className={`flex items-center text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            <span className="ml-1">{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-2xl font-bold text-foreground">{value}</p>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-12">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: brandColor }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={TrendingUp}
          label="Total Requests"
          value={analytics.totalRequests}
          subtitle={`${analytics.requestsByStatus.open} open`}
        />
        <StatCard
          icon={Users}
          label="Total Votes"
          value={analytics.totalVotes}
          subtitle={`${(analytics.totalVotes / Math.max(analytics.totalRequests, 1)).toFixed(1)} avg per request`}
        />
        <StatCard
          icon={MessageSquare}
          label="Total Comments"
          value={analytics.totalComments}
          subtitle={`${(analytics.totalComments / Math.max(analytics.totalRequests, 1)).toFixed(1)} avg per request`}
        />
        <StatCard
          icon={Clock}
          label="In Progress"
          value={analytics.requestsByStatus.in_progress}
          subtitle={`${analytics.requestsByStatus.completed} completed`}
        />
      </div>

      {/* Status Breakdown */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Requests by Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(analytics.requestsByStatus).map(([status, count]) => (
            <div key={status} className="text-center p-4 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-foreground">{count}</p>
              <p className="text-sm text-muted-foreground">
                {formatStatus(status)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Requests */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Top Voted Requests
          </h3>
          {analytics.topRequests.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No requests yet</p>
          ) : (
            <div className="space-y-3">
              {analytics.topRequests.map((request, index) => (
                <div
                  key={request.id}
                  onClick={() => onViewRequest(request)}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                >
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center">
                      <span className="text-xs font-semibold text-muted-foreground">
                        {index + 1}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate hover:text-primary transition-colors">
                      {request.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {request.voteCount} {request.voteCount === 1 ? 'vote' : 'votes'}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: `${brandColor}15`,
                          color: brandColor,
                        }}
                      >
                        {formatStatus(request.status)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Requests */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Recent Requests
          </h3>
          {analytics.recentRequests.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No requests yet</p>
          ) : (
            <div className="space-y-3">
              {analytics.recentRequests.map((request) => (
                <div
                  key={request.id}
                  onClick={() => onViewRequest(request)}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate hover:text-primary transition-colors">{request.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {new Date(request.createdAt.toDate()).toLocaleDateString()}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        by {request.createdByNickname || request.createdByEmail}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        {request.voteCount} {request.voteCount === 1 ? 'vote' : 'votes'}
                      </span>
                    </div>
                  </div>
                  <span
                    className="text-xs px-2 py-1 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: `${brandColor}15`,
                      color: brandColor,
                    }}
                  >
                    {formatStatus(request.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
